import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { NotificationPreference } from "@syllarys/contracts";
import { apiClient, useEms } from "../state/EmsContext";

const events = [
  ["workflow_assignment", "Affectations"],
  ["workflow_mention", "Mentions"],
  ["workflow_comment", "Commentaires"],
  ["workflow_publication", "Publications"],
  ["workflow_due", "Échéances"],
  ["job_failure", "Échecs de traitement"],
] as const;

export function NotificationPreferences(): React.ReactNode {
  const { session } = useEms();
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState("");
  const query = useQuery({
    queryKey: ["notification-preferences", session?.brand.id, session?.user.id],
    queryFn: () =>
      apiClient.listNotificationPreferences(session?.brand.id ?? ""),
    enabled: Boolean(session),
  });
  async function save(value: NotificationPreference): Promise<void> {
    if (!session || busy) return;
    setBusy(true);
    try {
      await apiClient.saveNotificationPreference(
        session.brand.id,
        value.eventType,
        value,
      );
      await query.refetch();
      setFeedback("Préférences enregistrées pour les prochains événements.");
    } catch (error) {
      setFeedback(
        error instanceof Error ? error.message : "Enregistrement impossible.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="notification-preferences">
      <summary>Mes préférences de notification</summary>
      <p>
        Les e-mails contiennent un lien vers l’espace connecté, jamais les notes
        internes. Un envoi désactivé ou non configuré n’est pas présenté comme
        réussi.
      </p>
      <p>
        Récapitulatifs : chaque jour à 08:00 UTC, ou le lundi à 08:00 UTC. La
        lecture d’une notification ne résout pas l’action.
      </p>
      {query.isLoading ? (
        <p role="status">Chargement des préférences…</p>
      ) : query.isError ? (
        <p role="alert">
          Chargement impossible.{" "}
          <button
            className="secondary-button"
            type="button"
            onClick={() => {
              void query.refetch();
            }}
          >
            Réessayer
          </button>
        </p>
      ) : (
        events.map(([eventType, label]) => {
          const value = query.data?.find(
            (entry) => entry.eventType === eventType,
          ) ?? {
            eventType,
            inApp: true,
            email: false,
            digest: "immediate" as const,
          };
          return (
            <fieldset key={eventType} disabled={busy}>
              <legend>{label}</legend>
              <label>
                <input
                  type="checkbox"
                  checked={value.inApp}
                  onChange={(event) =>
                    void save({ ...value, inApp: event.target.checked })
                  }
                />
                Dans l’application
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={value.email}
                  onChange={(event) =>
                    void save({ ...value, email: event.target.checked })
                  }
                />
                Par e-mail
              </label>
              <label>
                Fréquence des e-mails
                <select
                  value={value.digest}
                  disabled={!value.email || busy}
                  onChange={(event) =>
                    void save({
                      ...value,
                      digest: event.target
                        .value as NotificationPreference["digest"],
                    })
                  }
                >
                  <option value="immediate">À chaque événement</option>
                  <option value="daily">Récapitulatif quotidien</option>
                  <option value="weekly">Récapitulatif hebdomadaire</option>
                  <option value="off">Aucun envoi</option>
                </select>
              </label>
            </fieldset>
          );
        })
      )}
      {feedback ? <p role="status">{feedback}</p> : null}
    </details>
  );
}
